import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { heroData } from '../mock/data';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentSlide((prev) => (prev + 1) % heroData.slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 420 : -420,
      opacity: 0,
      scale: 0.98,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (dir) => ({
      zIndex: 0,
      x: dir < 0 ? 420 : -420,
      opacity: 0,
      scale: 0.98,
    }),
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentSlide((prev) => (prev + newDirection + heroData.slides.length) % heroData.slides.length);
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentSlide}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 280, damping: 32 },
            opacity: { duration: 0.4 },
            scale: { duration: 0.4 },
          }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${heroData.slides[currentSlide].image}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/95 via-purple-900/90 to-pink-900/85" />
          <div className="absolute inset-0 bg-slate-950/25" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/35 via-transparent to-slate-950/75" />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-16 -left-16 w-72 h-72 sm:w-96 sm:h-96 bg-gradient-to-br from-cyan-500/15 to-blue-500/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -right-16 w-72 h-72 sm:w-96 sm:h-96 bg-gradient-to-br from-pink-500/15 to-purple-500/15 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 md:py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 40 }}
          transition={{ duration: 0.7 }}
        >
          <motion.div
            key={`slide-title-${currentSlide}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="mb-3 sm:mb-4"
          >
            <span className="inline-block px-4 py-1.5 sm:px-5 sm:py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-cyan-200 font-bold text-[10px] sm:text-xs tracking-widest uppercase">
              {heroData.slides[currentSlide].title}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.65, delay: 0.12 }}
            className="text-white mb-3 sm:mb-4 leading-tight px-1"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            <span className="block text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              {heroData.title}
            </span>
            <span className="mt-1 sm:mt-2 block text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-pink-400 to-purple-400">
              {heroData.titleHighlight}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.55, delay: 0.22 }}
            className="text-sm sm:text-base md:text-lg text-gray-200 mb-2 max-w-md md:max-w-xl mx-auto font-light tracking-wide leading-snug"
            style={{ fontFamily: '"Inter", sans-serif' }}
          >
            {heroData.subtitle}
          </motion.p>

          <motion.p
            key={`slide-sub-${currentSlide}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-xs sm:text-sm md:text-base text-cyan-100/90 mb-8 sm:mb-9 max-w-sm sm:max-w-md mx-auto font-medium leading-relaxed"
          >
            {heroData.slides[currentSlide].subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.28 }}
            className="flex flex-col sm:flex-row gap-3 justify-center items-center"
          >
            <motion.a
              href="https://secure.transactworld.com/merchant/signup.jsp"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03, boxShadow: '0 16px 36px rgba(6, 182, 212, 0.35)' }}
              whileTap={{ scale: 0.98 }}
              className="group relative w-full sm:w-auto px-7 py-3.5 sm:py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-bold text-sm sm:text-base shadow-lg shadow-cyan-500/35 overflow-hidden"
            >
              <span className="relative z-10 inline-flex items-center justify-center gap-2">
                <span>Gateway Sign Up</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform shrink-0" />
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.28 }}
              />
            </motion.a>

            <motion.a
              href="https://account.payswix.com/register?ref=A00031"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03, boxShadow: '0 16px 36px rgba(236, 72, 153, 0.28)' }}
              whileTap={{ scale: 0.98 }}
              className="group relative w-full sm:w-auto px-7 py-3.5 bg-white/10 backdrop-blur-md text-white rounded-xl font-bold text-sm sm:text-base border-2 border-white/25 hover:border-pink-300/60 overflow-hidden"
            >
              <span className="relative z-10 inline-flex items-center justify-center gap-2">
                <span>IBAN Sign Up</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform shrink-0" />
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-pink-500/18 to-purple-500/18"
                initial={{ scale: 0 }}
                whileHover={{ scale: 1 }}
                transition={{ duration: 0.28 }}
              />
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-10 sm:mt-12 flex flex-wrap justify-center gap-3 sm:gap-5 text-gray-200"
          >
            {['PCI DSS Certified', '99.9% Uptime', '200+ Countries'].map((item, idx) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + idx * 0.08 }}
                className="flex items-center gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10"
              >
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full shrink-0" />
                <span className="text-[11px] sm:text-xs font-semibold whitespace-nowrap">{item}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 sm:gap-4">
        <motion.button
          type="button"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          onClick={() => paginate(-1)}
          className="w-10 h-10 sm:w-11 sm:h-11 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 hover:bg-white/18 transition-colors"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </motion.button>

        <div className="flex items-center gap-1.5">
          {heroData.slides.map((_, idx) => (
            <motion.button
              key={idx}
              type="button"
              onClick={() => {
                setDirection(idx > currentSlide ? 1 : -1);
                setCurrentSlide(idx);
              }}
              className={`transition-all duration-300 rounded-full ${
                idx === currentSlide
                  ? 'w-9 sm:w-10 h-2 bg-gradient-to-r from-cyan-400 to-pink-500'
                  : 'w-2 h-2 bg-white/30 hover:bg-white/50'
              }`}
              whileHover={{ scale: 1.12 }}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

        <motion.button
          type="button"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          onClick={() => paginate(1)}
          className="w-10 h-10 sm:w-11 sm:h-11 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 hover:bg-white/18 transition-colors"
          aria-label="Next slide"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
